import { useCallback, useEffect, useState } from 'react'
import { Button, Chip, Switch } from '@heroui/react'
import { api } from '../lib/api'
import { defaultRate, keyStateLabel } from '../lib/bookingAdmin'
import { optionOn } from '../lib/orgOptions'
import { formatMoment } from '../lib/staffText'
import type { BookingKey } from '../lib/types'
import { Icon } from '../ui/icons'
import { explain } from '../lib/staffErrors'
import { EmptyState, StatusChip } from '../ui/parts'
import { useSession } from '../ui/Session'
import { useStaffText } from '../ui/StaffText'
import { Mark, OneTimeSecret } from './connectParts'
import { Field, Messages, Num, Text, useAction } from './parts'
import { Toggle, useFlash, useSave } from './sectionKit'
import { IconTile, InnerPage, ListCard, ListRow, ListTable, NameCell, Notice, PageHead, useOpenRecord } from './settingsKit'
import '../styles/connect.css'

/**
 * Administration → Online booking → API keys. A new key is shown once, with
 * Copy; the server keeps only its hash. Trusted is fixed at creation.
 *
 * locked is true only while the licence is locked and this organisation still
 * holds the module by its document — Administration's bookingLocked. The
 * server accepts only a key already on being switched off, and a delete,
 * through a lock; everything else here is disabled to match, rather than left
 * live to answer LICENCE_LOCKED.
 */
export function BookingKeys({ locked }: { locked?: boolean }) {
  const { lang, say } = useStaffText()
  const { flash, toast } = useFlash()
  const bookingOff = !optionOn(useSession()?.identity, 'onlineBooking')
  const [keys, setKeys] = useState<BookingKey[]>([])
  const [secret, setSecret] = useState('')
  const [madeName, setMadeName] = useState('')
  const [deleting, setDeleting] = useState('')
  const load = useCallback(async () => setKeys((await api.bookingKeys()).keys), [])
  const { busy, error, notice, run, setError } = useAction(load)
  useEffect(() => {
    load().catch((err: unknown) => {
      setKeys([])
      setError(explain(err, lang, 'common.unreachable'))
    })
  }, [load, lang, setError])

  // A new key is made on its own page (#bookingKeys/new); a locked licence makes none.
  const rec = useOpenRecord<BookingKey>([], (k) => k.id, true)
  const editing = rec.isNew && !locked
  if (editing) {
    return (
      <>
        {toast}
        <NewKeyDialog
          key={rec.code}
          flash={flash}
          onClose={rec.close}
          onMade={async (made, name) => {
            setSecret(made)
            setMadeName(name)
            rec.close()
            await load()
          }}
        />
      </>
    )
  }

  return (
    <>
      <PageHead
        title={say('booking.section.keys')}
        line={say('connect.line.bookingKeys')}
        actions={
          <Button variant="primary" isDisabled={locked} onPress={() => rec.open()}>
            <Icon name="plus" size={16} /> {say('booking.key.create')}
          </Button>
        }
      />
      <Messages error={error} notice={notice} />
      {bookingOff && (
        <p className="connect-callout connect-gap">
          <Icon name="alert" size={15} /> {say('connect.bookingOff')}
        </p>
      )}
      {locked && <p className="connect-callout connect-gap">{say('booking.locked')}</p>}
      {secret && (
        <div className="connect-gap">
          <OneTimeSecret
            title={say('booking.key.shownOnce')}
            note={madeName}
            value={secret}
            labels={{ show: say('connect.show'), hide: say('connect.hide'), copy: say('booking.key.copy'), done: say('ui.close') }}
            onCopied={() => flash(say('common.copied'))}
            onDone={() => setSecret('')}
          />
        </div>
      )}

      <ListCard title={say('connect.keys.list')} count={say('connect.keyCount', { n: keys.length })}>
        {keys.length === 0 ? (
          <EmptyState icon={<Mark name="key" size={20} />} title={say('booking.key.none')} />
        ) : (
          <ListTable
            label={say('connect.keys.list')}
            cols="minmax(0, 1.4fr) 130px 90px minmax(0, 1fr) 150px minmax(44px, auto)"
            head={[
              say('common.name'),
              say('connect.col.access'),
              say('booking.key.rate'),
              say('booking.key.lastUsed'),
              say('booking.key.active'),
              '',
            ]}
          >
            {keys.map((k) => (
              <ListRow key={k.id} off={!k.active}>
                <NameCell tile={<IconTile icon="key" tone={k.trusted ? 2 : 4} size="sm" />} name={k.name} />
                <span>
                  <Chip size="sm" variant="secondary">
                    {say(k.trusted ? 'booking.key.trusted' : 'booking.key.needsCode')}
                  </Chip>
                </span>
                <span className="set-num">{k.perMinute}</span>
                <span className="set-muted">
                  {k.lastUsedAt ? formatMoment(k.lastUsedAt, lang, { date: true, time: true }) : say('booking.key.never')}
                </span>
                <span>
                  <Switch
                    className="connect-keystate"
                    isSelected={k.active}
                    aria-label={say(keyStateLabel(k.active))}
                    // Turning a key off stays live through a lock; turning one back on
                    // is a write the server refuses with LICENCE_LOCKED.
                    isDisabled={busy || (locked && !k.active)}
                    onChange={() => void run(() => api.updateBookingKey(k.id, { active: !k.active }), say('common.saved'))}
                  >
                    <Switch.Content className="sw-hit">
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                      <StatusChip tone={k.active ? 'ok' : undefined}>{say(keyStateLabel(k.active))}</StatusChip>
                    </Switch.Content>
                  </Switch>
                </span>
                <span className="connect-rowact">
                  {deleting === k.id ? (
                    <span className="admin-inline" role="alertdialog" aria-label={say('common.delete')}>
                      <span>{say('booking.key.deleteAsk', { name: k.name })}</span>
                      <Button variant="ghost" size="sm" onPress={() => setDeleting('')}>
                        {say('common.keep')}
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        isDisabled={busy}
                        onPress={() => void run(() => api.deleteBookingKey(k.id), say('common.saved')).then(() => setDeleting(''))}
                      >
                        {say('common.delete')}
                      </Button>
                    </span>
                  ) : (
                    <Button isIconOnly variant="ghost" size="sm" aria-label={say('common.delete')} onPress={() => setDeleting(k.id)}>
                      <Icon name="trash" size={17} />
                    </Button>
                  )}
                </span>
              </ListRow>
            ))}
          </ListTable>
        )}
      </ListCard>
      {toast}
    </>
  )
}

function NewKeyDialog({
  flash,
  onClose,
  onMade,
}: {
  flash: (text: string) => void
  onClose: () => void
  onMade: (secret: string, name: string) => Promise<void>
}) {
  const { say } = useStaffText()
  const [name, setName] = useState('')
  const [trusted, setTrusted] = useState(false)
  const [rate, setRate] = useState(defaultRate(false))
  const noop = useCallback(() => {}, [])
  const { busy, error, setError, save } = useSave(noop, flash)

  async function create() {
    if (name.trim().length < 1 || name.trim().length > 80) return setError(say('booking.problem.keyName'))
    if (rate < 1 || rate > 6000) return setError(say('booking.problem.rate'))
    let made = ''
    const ok = await save(async () => {
      made = (await api.createBookingKey({ name: name.trim(), trusted, perMinute: rate })).secret
    }, say('common.saved'))
    if (ok) await onMade(made, name.trim())
  }

  return (
    <InnerPage
      back={say('booking.section.keys')}
      title={say('booking.key.create')}
      onClose={onClose}
      onSubmit={() => void create()}
      submitLabel={say('booking.key.create')}
      valid={name.trim() !== ''}
      busy={busy}
    >
      {error && <Notice>{error}</Notice>}
      <Field label={say('common.name')}>
        <Text value={name} onChange={setName} />
      </Field>
      <Toggle
        title={say('booking.key.trusted')}
        checked={trusted}
        onChange={(v) => {
          setTrusted(v)
          setRate(defaultRate(v))
        }}
      />
      <Field label={say('booking.key.rate')}>
        <Num value={rate} min={1} max={6000} onChange={setRate} />
      </Field>
    </InnerPage>
  )
}
